import {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  memo,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { AppContext } from "./AppContext.tsx";

const HistoryEntry = memo(
  forwardRef(
    (
      {
        conversationId,
        isFavorite,
        isSelected,
        onSelect,
        extended = false,
        title = "New conversation",
        subtitle = "...",
        isNested = false,
        isTabbable = false,
      },
      ref
    ) => {
      const { deleteConversation, favoriteConversation, updateTitle, showConfirmation, selectedConversation } =
        useContext(AppContext);
      const navigate = useNavigate();

      /* ────────────────────────────── refs ────────────────────────────── */
      const localRef = useRef(null);
      const historyEntryRef = ref || localRef;
      const titleRef = useRef(null);
      const subtitleRef = useRef(null);
      const inputRef = useRef(null);

      /* ───────────────────────────── state ────────────────────────────── */
      const [isEditingTitle, setIsEditingTitle] = useState(false);
      const [entryTitle, setEntryTitle] = useState(title);
      const [isTitleTruncated, setIsTitleTruncated] = useState(false);
      const [isSubtitleTruncated, setIsSubtitleTruncated] = useState(false);
      const [isDeleting, setIsDeleting] = useState(false);

      /* ─────────────────────────── handlers ──────────────────────────── */
      // activate entry with Enter / Space while *not* editing
      const handleRootKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(conversationId);
        }
      };

      // while editing: Enter = save, Esc = cancel
      const handleKeyDown = useCallback(
        async (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (entryTitle.trim()) {
              await updateTitle(conversationId, entryTitle);
              setIsEditingTitle(false);
            }
          } else if (e.key === "Escape") {
            setIsEditingTitle(false);
            setEntryTitle(title); // revert
          }
        },
        [entryTitle, updateTitle, conversationId, title]
      );

      // Handle double-click to edit title
      const handleDoubleClick = useCallback((e) => {
        e.stopPropagation();
        setEntryTitle(title);
        setIsEditingTitle(true);
      }, [title]);


      // Context menu handlers
      const handleFavoriteToggle = useCallback(async () => {
        await favoriteConversation(conversationId);
        const message = isFavorite 
          ? "Conversation removed from favorites" 
          : "Conversation added to favorites";
        toast.success(message);
      }, [favoriteConversation, conversationId, isFavorite]);

      const handleDelete = useCallback(async () => {
        await showConfirmation({
          title: 'Delete Conversation',
          message: `Are you sure you want to delete "${title}"? This action cannot be undone.`,
          confirmText: 'Delete',
          cancelText: 'Cancel',
          variant: 'destructive',
          isLoading: isDeleting,
          onConfirm: async () => {
            setIsDeleting(true);
            try {
              const success = await deleteConversation(conversationId);
              if (success) {
                toast.success("Conversation deleted");
                // Navigate to chat home if we deleted the current conversation
                if (selectedConversation?.conversationId === conversationId) {
                  navigate('/chat');
                }
              } else {
                toast.error("Failed to delete conversation");
              }
            } finally {
              setIsDeleting(false);
            }
          }
        });
      }, [showConfirmation, title, deleteConversation, conversationId, selectedConversation, navigate, isDeleting]);

      /* ────────────────────── click-outside cancel ───────────────────── */
      useEffect(() => {
        if (!isEditingTitle) return;
        const handleClickOutside = (event) => {
          if (
            historyEntryRef.current &&
            !historyEntryRef.current.contains(event.target)
          ) {
            setIsEditingTitle(false);
            setEntryTitle(title); // revert
          }
        };
        document.addEventListener("mousedown", handleClickOutside, {
          passive: true,
        });
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }, [isEditingTitle, title, historyEntryRef]);

      /* ──────────────────────── focus the input ──────────────────────── */
      useEffect(() => {
        if (isEditingTitle && inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, [isEditingTitle]);

      /* ─────── figure out if title / subtitle are truncated ─────── */
      useEffect(() => {
        if (titleRef.current) {
          setIsTitleTruncated(
            titleRef.current.scrollWidth > titleRef.current.clientWidth
          );
        }
        if (subtitleRef.current) {
          setIsSubtitleTruncated(
            subtitleRef.current.scrollWidth > subtitleRef.current.clientWidth
          );
        }
      }, [title, subtitle, extended, isEditingTitle]);

      /* ─────────────────────────── render ──────────────────────────── */
      return (
        <div
          ref={historyEntryRef}
          data-nav-item
          role="button"
          tabIndex={isTabbable || isSelected ? 0 : -1}
          aria-label={`Conversation: ${title}`}
          aria-selected={isSelected}
          onKeyDown={!isEditingTitle ? handleRootKey : undefined}
          className={`flex flex-row items-center justify-between rounded text-sm hover:cursor-pointer focus:outline-none truncate w-full transition-colors duration-200 relative group ${
            isSelected ? "bg-bg-selected" : ""
          } ${extended ? "h-16" : "h-7"} ${
            isNested
              ? "pl-spacing-6"
              : "p-spacing-2 focus:bg-bg-hover hover:bg-bg-hover"
          }`}
          onClick={() => !isEditingTitle && onSelect(conversationId)}
        >
          {isNested && (
            <div className="absolute left-spacing-6 right-0 top-0 bottom-0 rounded group-hover:bg-bg-hover group-focus:bg-bg-hover transition-colors duration-200 pointer-events-none" />
          )}
          <div className={`flex flex-row items-center justify-between w-full ${isNested ? "p-spacing-2 relative z-10" : ""}`}>
            {/* ───────────────── left side (title / subtitle) ──────────────── */}
            {extended ? (
              <div className="flex flex-col gap-spacing-1 truncate align-center">
                <span
                  ref={titleRef}
                  title={isTitleTruncated ? title : undefined}
                  className="text-primary truncate"
                >
                  {title}
                </span>
                {subtitle && (
                  <span
                    ref={subtitleRef}
                    title={isSubtitleTruncated ? subtitle : undefined}
                    className="text-sm truncate font-light text-secondary max-w-[80%]"
                  >
                    {subtitle}
                  </span>
                )}
              </div>
            ) : !isEditingTitle ? (
              <span
                ref={titleRef}
                title={isTitleTruncated ? title : undefined}
                className="w-full truncate"
                onDoubleClick={handleDoubleClick}
              >
                {title}
              </span>
            ) : (
              <input
                ref={inputRef}
                autoFocus
                value={entryTitle}
                onKeyDown={handleKeyDown}
                onChange={(e) => setEntryTitle(e.target.value)}
                aria-label="Edit conversation title"
                className={`text-sm w-full outline-none focus-visible:ring-2 focus-visible:ring-accent rounded px-1 ${
                  isSelected ? "bg-bg-active" : "bg-bg-hover"
                }`}
                placeholder="Conversation title"
                type="text"
              />
            )}
          </div>
        </div>
      );
    }
  )
);

HistoryEntry.propTypes = {
  conversationId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  updatedAt: PropTypes.string,
  isFavorite: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  extended: PropTypes.bool,
  isNested: PropTypes.bool,
  isTabbable: PropTypes.bool,
};

HistoryEntry.displayName = "HistoryEntry";

export default HistoryEntry;
